import clsx from 'clsx';
import { memo } from 'react';
import { PenSquare, Archive, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import EstadoBadge from './EstadoBadge.jsx';

const sortableColumns = ['apellido', 'legajo', 'fechaIngreso'];

function formatDate(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('es-AR').format(new Date(value));
}

// Tabla de empleados con acciones y ordenamiento
function EmpleadosTableComponent({ rows, loading, sort, onSortChange, onEdit, onDelete, onRestore }) {
  const handleSort = field => {
    if (!sortableColumns.includes(field)) return;
    const nextDirection = sort.field === field && sort.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ field, direction: nextDirection });
  };

  return (
    <div className="overflow-hidden rounded-3xl border border-[var(--border)] bg-[var(--panel)] shadow-[var(--shadow-soft)]">
      <table className="min-w-full divide-y divide-[var(--border)] text-left">
        <thead className="bg-gradient-to-r from-brand-navy/90 to-brand-lime/40 text-white dark:from-brand-navy dark:to-emerald-600">
          <tr>
            {['legajo', 'apellido', null, null, null, null, null, 'fechaIngreso', null].map((field, index) => {
              const labels = [
                'Legajo',
                'Colaborador',
                'Documento',
                'Empresa',
                'Sucursal',
                'Puesto',
                'Estado',
                'Ingreso',
                'Acciones',
              ];
              const label = labels[index];
              const isSortable = sortableColumns.includes(field);
              const isActive = isSortable && sort.field === field;
              return (
                <th key={label} className={clsx('px-6 py-4 text-xs font-semibold uppercase tracking-wide', index === 8 ? 'text-right' : 'text-left')}>
                  {isSortable ? (
                    <button
                      type="button"
                      onClick={() => handleSort(field)}
                      className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/90 transition hover:text-white"
                    >
                      {label}
                      {isActive && <span aria-hidden="true">{sort.direction === 'asc' ? '↑' : '↓'}</span>}
                    </button>
                  ) : (
                    label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--border)] bg-[var(--panel)]">
          {loading ? (
            <tr>
              <td colSpan={9} className="px-6 py-12 text-center text-sm text-[var(--text-muted)]">
                Cargando empleados...
              </td>
            </tr>
          ) : rows.length ? (
            rows.map(row => {
              const nombreCompleto = `${row.apellido}, ${row.nombre}`;
              return (
                <tr key={row.id} className="transition hover:bg-brand-teal/5 dark:hover:bg-emerald-500/10">
                  <td className="px-6 py-4 text-sm font-semibold text-brand-navy">
                    {row.legajo || '—'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-[var(--text-primary)]">{nombreCompleto}</span>
                      <span className="text-xs text-[var(--text-muted)]">{row.email || 'sin email'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-[var(--text-muted)]">
                    {row.dni || '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-[var(--text-muted)]">{row.empresaId}</td>
                  <td className="px-6 py-4 text-sm text-[var(--text-muted)]">{row.sucursalId || '—'}</td>
                  <td className="px-6 py-4 text-sm text-[var(--text-muted)]">{row.puesto || '—'}</td>
                  <td className="px-6 py-4">
                    <EstadoBadge estado={row.estado} />
                  </td>
                  <td className="px-6 py-4 text-sm text-[var(--text-muted)]">
                    {formatDate(row.fechaIngreso)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        className="rounded-2xl bg-brand-teal/10 text-brand-teal hover:bg-brand-teal/20"
                        size="sm"
                        onClick={() => onEdit(row.id)}
                      >
                        <PenSquare size={16} />
                        Editar
                      </Button>
                      {row.estado === 'BAJA' ? (
                        <Button
                          variant="ghost"
                          className="rounded-2xl bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20"
                          size="sm"
                          onClick={() => onRestore(row.id)}
                        >
                          <RotateCcw size={16} />
                          Restaurar
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          className="rounded-2xl bg-rose-500/10 text-rose-500 hover:bg-rose-500/20"
                          size="sm"
                          onClick={() => onDelete(row.id)}
                        >
                          <Archive size={16} />
                          Baja
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={9} className="px-6 py-12 text-center text-sm text-[var(--text-muted)]">
                No encontramos resultados con los filtros actuales.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export const EmpleadosTable = memo(EmpleadosTableComponent);
export default EmpleadosTable;
